import {
  Avatar,
  AvatarFallback,
} from "@arcadeai/design-system/components/ui/atoms/avatar";
import { Badge } from "@arcadeai/design-system/components/ui/atoms/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import type { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import { useOrgPermissions } from "@/app/capabilities/api/hooks";
import { ORG } from "@/app/capabilities/permissions";
import type { MemberProject, OrgMember } from "@/app/org-members/types";
import { RoleBadge } from "@/app/roles/components/role-badge";
import { DataTable } from "@/components/custom/table/data-table";
import { DataTableColumnHeader } from "@/components/custom/table/data-table-column-header";
import { DataTableContent } from "@/components/custom/table/data-table-content";
import { DataTablePagination } from "@/components/custom/table/data-table-pagination";
import { useSession } from "@/lib/auth-client";
import { getInitials } from "@/lib/utils";
import { OrgMemberRoleSwitcher } from "./org-member-role-switcher";

const OrgMemberNameCell = ({ member }: { member: OrgMember }) => {
  const { data: session } = useSession();
  const isCurrentUser = session?.user.accountId === member.account_id;
  const displayName = member.name ?? member.email;

  return (
    <div className="flex items-center gap-3">
      <Avatar className="h-8 w-8 border">
        <AvatarFallback className="text-xs">
          {getInitials(displayName)}
        </AvatarFallback>
      </Avatar>
      <span className={isCurrentUser ? "font-semibold" : "font-medium"}>
        {displayName}
      </span>
      {isCurrentUser && <Badge variant="default">You</Badge>}
    </div>
  );
};

const OrgMemberRoleCell = ({ member }: { member: OrgMember }) => {
  const { canOrg } = useOrgPermissions();

  if (canOrg(ORG.members.update)) {
    return <OrgMemberRoleSwitcher member={member} />;
  }

  return member.org_role ? (
    <RoleBadge role={member.org_role} />
  ) : (
    <span className="inline-flex items-center rounded border border-border bg-muted/40 px-1.5 py-0.5 font-medium text-muted-foreground text-xs">
      Member
    </span>
  );
};

const ProjectBadge = ({ name }: { name: string }) => (
  <Badge className="max-w-[12rem] font-normal" variant="outline">
    <span className="truncate">{name}</span>
  </Badge>
);

const MemberProjectsCell = ({ projects }: { projects: MemberProject[] }) => {
  if (projects.length === 0) {
    return <span className="text-muted-foreground text-sm">—</span>;
  }

  const [first, ...rest] = projects;

  return (
    <div className="flex items-center gap-1.5">
      <ProjectBadge name={first.name} />
      {rest.length > 0 && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger render={<span className="inline-flex" />}>
              <Badge className="cursor-default font-normal" variant="secondary">
                +{rest.length}
              </Badge>
            </TooltipTrigger>
            <TooltipContent className="flex flex-col gap-1">
              {rest.map((project) => (
                <span key={project.project_id}>{project.name}</span>
              ))}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
};

export function OrgMembersTable({ members }: { members: OrgMember[] }) {
  const columns = useMemo<ColumnDef<OrgMember>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        cell: ({ row }) => <OrgMemberNameCell member={row.original} />,
      },
      {
        accessorKey: "email",
        header: "Email",
        cell: ({ row }) => (
          <span className="text-muted-foreground">{row.original.email}</span>
        ),
      },
      {
        id: "role",
        accessorFn: (row) => row.org_role?.display_name ?? "Member",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Role" />
        ),
        cell: ({ row }) => <OrgMemberRoleCell member={row.original} />,
      },
      {
        id: "projects",
        enableSorting: false,
        header: "Projects",
        cell: ({ row }) => (
          <MemberProjectsCell projects={row.original.projects} />
        ),
      },
    ],
    []
  );

  return (
    <DataTable
      className="space-y-4"
      columns={columns}
      data={members}
      defaultPageSize={10}
      getRowId={(row) => row.account_id}
    >
      <DataTableContent stickyColumnBg="card" />
      <DataTablePagination />
    </DataTable>
  );
}
